import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getService } from '../services/supabase';
import { AddOn } from '../types';

const PaymentPage: React.FC = () => {
  const location = useLocation();
  const navState = (location.state as any) || {};
  const booking = navState.booking || (() => {
    try { const raw = sessionStorage.getItem('pendingBooking'); if (raw) return JSON.parse(raw); } catch(_) {}
    return null;
  })();
  const initialMeta = navState.meta || {};
  const [serviceDetails, setServiceDetails] = useState<any>(initialMeta.service || null);
  const [resolvedTravelFee, setResolvedTravelFee] = useState<number>(
    initialMeta.travelFee ?? booking?.travel_fee ?? booking?.travelFee ?? 0
  );
  const [resolvedTravelMiles, setResolvedTravelMiles] = useState<number>(
    initialMeta.travelMiles ?? booking?.travel_miles ?? booking?.travelMiles ?? 0
  );
  const [sessionRequested, setSessionRequested] = useState(false);

  useEffect(() => {
    if (!booking) return;
    if (initialMeta.travelFee == null && booking.travel_fee != null) {
      setResolvedTravelFee(booking.travel_fee);
    }
    if (initialMeta.travelMiles == null && booking.travel_miles != null) {
      setResolvedTravelMiles(booking.travel_miles);
    }
  }, [booking, initialMeta.travelFee, initialMeta.travelMiles]);

  useEffect(() => {
    if (!booking) {
      console.log('No booking found. Please start from the booking flow.');
      return;
    }
    // Logging booking info for debugging
    console.log('Incoming booking:', booking);
    if (booking.address && booking.city && booking.state) {
      console.log('Incoming origin:', '7230 Jemmah Lane, Jacksonville, Florida');
      console.log('Incoming destination:', `${booking.address}, ${booking.city}, ${booking.state}`);
    }
  }, [booking]);

  useEffect(() => {
    const resolveService = async () => {
      if (!booking || serviceDetails || !booking.service_id) return;
      try {
        const fetched = await getService(booking.service_id);
        setServiceDetails(fetched);
      } catch (err) {
        console.error('Failed to fetch service details for payment:', err);
      }
    };
    resolveService();
  }, [booking, serviceDetails]);

  useEffect(() => {
  if (!booking) return;
  if (sessionRequested) return;

    const meaningfulName =
      serviceDetails?.name ||
      booking?.service_name ||
      booking?.service?.name;

    if (!meaningfulName) {
      console.warn('Waiting for service details before creating Stripe session...');
      return;
    }

    const basePrice =
      serviceDetails?.price ??
      booking.service_price ??
      booking.service?.price ??
      booking.total_amount ?? 0;

    const servicePayload = {
      name:
        serviceDetails?.name ||
        booking.service_name ||
        booking.service?.name ||
        'Service',
      description:
        serviceDetails?.description ||
        booking.service_description ||
        booking.service?.description ||
        '',
      price: basePrice
    };

    const serviceName = (servicePayload.name || '').trim();
    if (!serviceName) {
      console.warn('Stripe payload missing service name, waiting for service details...');
      return;
    }

    const addOns: AddOn[] = Array.isArray(initialMeta.addOns)
      ? initialMeta.addOns
      : Array.isArray(booking.add_ons)
        ? booking.add_ons
        : [];

    const addOnTotal = addOns.reduce((sum, addOn) => sum + (addOn?.price ?? 0), 0);
    const computedTotal = Number((basePrice + addOnTotal + resolvedTravelFee).toFixed(2));

    const checkoutBooking = {
      ...booking,
      service_id: booking.service_id,
      service_price: basePrice,
      total_amount: computedTotal,
      travel_fee: resolvedTravelFee,
      travel_miles: resolvedTravelMiles,
      add_ons: addOns,
      payment_status: 'pending',
      status: booking.status || 'pending'
    };

    try {
      sessionStorage.setItem('pendingBooking', JSON.stringify(checkoutBooking));
    } catch (persistErr) {
      console.warn('Failed to persist checkout booking state:', persistErr);
    }

    const stripePayload = {
      service: servicePayload,
      addOns,
      travelFee: resolvedTravelFee,
      travelMiles: resolvedTravelMiles,
      booking: checkoutBooking
    };

    console.log('Stripe payload:', stripePayload);

    setSessionRequested(true);

    fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(stripePayload)
    })
      .then(async res => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (data.url) {
          setSessionRequested(true);
          window.location.href = data.url;
        } else {
          alert('Could not create Stripe Checkout session.');
          setSessionRequested(false);
        }
      })
      .catch(err => {
        console.error('Stripe Checkout error:', err);
        alert('There was an error redirecting to payment. Please try again.');
        setSessionRequested(false);
      });
  }, [booking, serviceDetails, resolvedTravelFee, resolvedTravelMiles, initialMeta.addOns, sessionRequested]);

  return (
    <div className="payment-page">
      <div className="loading-spinner"></div>
      <p>Redirecting to secure payment...</p>
    </div>
  );
};

export default PaymentPage;
